import { trackUpdatePayload, requestId } from "../api/trackPayloads";
import OpenInNew from "@mui/icons-material/OpenInNew";
import MuiLink from "@mui/material/Link";
import Divider from "@mui/material/Divider";
import Alert from "@mui/material/Alert";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import TableHead from "@mui/material/TableHead";
import Table from "@mui/material/Table";
import MenuItem from "@mui/material/MenuItem";
import { useDialogs } from "../ui/DialogProvider";
import useEditorForm from "../hooks/useEditorForm";
import TextInput from "../ui/TextInput";
import Button from "@mui/material/Button";
import React from "react";
import { useSearchParams } from "react-router-dom";
import Link from "../ui/RouterLink";
import {
  API_BASE,
  fetchJson,
  apiFetch,
  normalizeTags,
  fetchBreakOptions,
  fetchGenres,
  fetchSubGenres,
} from "../utils";
import TrackCard from "../components/TrackCard";
import TrackRow from "../components/TrackRow";
import TrackRowReadOnly from "../components/TrackRowReadOnly";
import MultiSelectDropdown from "../components/MultiSelectDropdown";
import AlbumQuantity from "../components/AlbumQuantity";
import CollectionActions from "../components/CollectionActions";
import AlbumOptionsMenu from "../components/AlbumOptionsMenu";
import { isCollectionQuantity } from "../utils/collection";
import "../styles/collection.css";
const sortUniqueStrings = (values) =>
  Array.from(new Set((values || []).filter(Boolean))).sort((a, b) =>
    a.localeCompare(b),
  );
const normalizeArrayField = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return sortUniqueStrings(value);
  }
  if (typeof value === "string") {
    return sortUniqueStrings(
      value
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean),
    );
  }
  return [];
};
function AlbumDetailsContent({ albumId, role, settings, username }) {
  const dialogs = useDialogs();
  const [searchParams] = useSearchParams();
  const [highlightTrack, setHighlightTrack] = React.useState(
    () => searchParams.get("track") || null,
  );
  const [album, setAlbum] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [retry, setRetry] = React.useState(0);
  const [saving, setSaving] = React.useState(false);
  const savePending = React.useRef(false);
  const fileInputRef = React.useRef(null);
  const [imgVersion, setImgVersion] = React.useState(Date.now());
  const [editMode, setEditMode] = React.useState("view"); // view | tracks | album
  const [albumFields, setAlbumFields, albumForm] = useEditorForm({
    title: "",
    genres: [],
    sub_genres: [],
  });
  const [editingField, setEditingField] = React.useState(null); // title | genres | sub_genres
  const titleRef = React.useRef(null);
  const [trackEdits, setTrackEdits, trackForm] = useEditorForm({});
  const [tagOptions, setTagOptions] = React.useState([]);
  const [genreOptions, setGenreOptions] = React.useState([]);
  const [subGenreOptions, setSubGenreOptions] = React.useState([]);
  const [albumMenuOpen, setAlbumMenuOpen] = React.useState(false);
  const albumMenuRef = React.useRef(null);
  const [breakOptions, setBreakOptions] = React.useState([]);
  const [optionError, setOptionError] = React.useState(null);
  const [optionRevision, setOptionRevision] = React.useState(0);
  const archived = album?.archive === true;
  const detailsHeading = React.useRef(null);
  const focusAfterChange = React.useRef(false);
  const [collectionMode, setCollectionMode] = React.useState(null);
  const canManageCollection =
    isCollectionQuantity(album?.quantity) &&
    ["basic", "user", "admin"].includes(role);
  React.useEffect(() => {
    if (focusAfterChange.current) {
      focusAfterChange.current = false;
      detailsHeading.current?.focus();
    }
  }, [album]);
  React.useEffect(() => {
    let active = true;
    setOptionError(null);
    const apply = (setter) => (value) => {
      if (active) setter(value);
    };
    const fail = (failure) => {
      if (active) setOptionError(failure);
    };
    fetchJson(`${API_BASE}/tags/`, {
      credentials: "include",
    })
      .then((data) => {
        const list = Array.isArray(data.tags) ? data.tags : data;
        if (active && Array.isArray(list)) {
          setTagOptions([...list].sort((a, b) => a.localeCompare(b)));
        }
      })
      .catch(fail);
    fetchBreakOptions().then(apply(setBreakOptions)).catch(fail);
    fetchGenres().then(apply(setGenreOptions)).catch(fail);
    fetchSubGenres().then(apply(setSubGenreOptions)).catch(fail);
    return () => {
      active = false;
    };
  }, [optionRevision]);
  React.useEffect(() => {
    if (editMode !== "view") {
      setAlbumMenuOpen(false);
    }
  }, [editMode]);
  React.useEffect(() => {
    if (!highlightTrack) return;
    const clear = () => setHighlightTrack(null);
    document.addEventListener("click", clear);
    document.addEventListener("keydown", clear);
    document.addEventListener("touchstart", clear);
    return () => {
      document.removeEventListener("click", clear);
      document.removeEventListener("keydown", clear);
      document.removeEventListener("touchstart", clear);
    };
  }, [highlightTrack]);
  React.useEffect(() => {
    if (!highlightTrack) return;
    if (window.matchMedia("(max-width: 600px)").matches) {
      const el = document.querySelector(".track-card.highlight");
      if (el) {
        el.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }
  }, [highlightTrack, album]);
  const handleAlbumMenuToggle = (e) => {
    if (albumMenuOpen) {
      e.currentTarget.blur();
    }
    setAlbumMenuOpen(!albumMenuOpen);
  };
  const canEditAlbum = !archived && (role === "admin" || role === "user");
  const canEditDuration = canEditAlbum;
  const canEditTitle = !archived && role === "admin";
  const canEditTrackInfo = !archived && role === "admin";
  const canEditGenres = !archived && role === "admin";
  const canEditSubGenres =
    !archived && (role === "admin" || role === "user" || role === "basic");
  const isMobile = window.matchMedia("(max-width: 600px)").matches;
  const hasAlbumMenuOptions =
    !archived &&
    (canEditTitle || canEditAlbum || canEditSubGenres || !isMobile);
  React.useEffect(() => {
    const controller = new AbortController();
    setError(null);
    fetchJson(`${API_BASE}/albums/${albumId}/`, {
      credentials: "include",
      signal: controller.signal,
    })
      .then((data) => {
        if (controller.signal.aborted) return;
        if (!data || !Array.isArray(data.tracks))
          throw new Error("Invalid album response");
        if (Array.isArray(data.genres)) {
          data.genres = data.genres.slice().sort((a, b) => a.localeCompare(b));
        }
        if (Array.isArray(data.sub_genres)) {
          data.sub_genres = data.sub_genres
            .slice()
            .sort((a, b) => a.localeCompare(b));
        }
        setAlbum(data);
      })
      .catch((failure) => {
        if (!controller.signal.aborted) setError(failure);
      });
    return () => controller.abort();
  }, [albumId, retry]);
  React.useEffect(() => {
    if (album) {
      const albumGenres = normalizeArrayField(album.genres);
      const albumSubGenres = normalizeArrayField(album.sub_genres);
      setAlbumFields({
        title: album.title || "",
        genres: albumGenres,
        sub_genres: albumSubGenres,
      });
      const tags = Array.from(
        new Set(album.tracks.flatMap((t) => normalizeTags(t.tags))),
      ).sort((a, b) => a.localeCompare(b));
      setTagOptions((opts) => {
        const merged = Array.from(new Set([...opts, ...tags]));
        return merged.sort((a, b) => a.localeCompare(b));
      });
      if (albumGenres.length) {
        setGenreOptions((opts) => sortUniqueStrings([...opts, ...albumGenres]));
      }
      if (albumSubGenres.length) {
        setSubGenreOptions((opts) =>
          sortUniqueStrings([...opts, ...albumSubGenres]),
        );
      }
    }
  }, [album, setAlbumFields]);
  const checkImageExists = async () => {
    const resp = await apiFetch(`${API_BASE}/albums/${albumId}/image/`, {
      method: "GET",
      redirect: "manual",
      credentials: "include",
    });
    if (resp.status === 404) return false;
    if (
      resp.ok ||
      resp.type === "opaqueredirect" ||
      (resp.status >= 300 && resp.status < 400)
    )
      return true;
    throw new Error(
      `Could not check the existing image (HTTP ${resp.status}).`,
    );
  };
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file || savePending.current) return;
    const input = e.target;
    savePending.current = true;
    setSaving(true);
    try {
      const exists = await checkImageExists();
      if (exists) {
        const confirmOverwrite = await dialogs.confirm(
          "Are you sure you want to overwrite the existing image?",
        );
        if (!confirmOverwrite) {
          return;
        }
      }
      const form = new FormData();
      form.append("image", file);
      await fetchJson(
        `${API_BASE}/albums/${albumId}/image/${exists ? "?overwrite=true" : ""}`,
        {
          method: "POST",
          body: form,
          credentials: "include",
        },
      );
      setImgVersion(Date.now());
    } catch (err) {
      dialogs.alert("Error uploading image: " + err.message);
    } finally {
      input.value = "";
      savePending.current = false;
      setSaving(false);
    }
  };
  const handleAlbumFieldChange = (field, value) => {
    if (field === "genres" || field === "sub_genres") {
      const nextValues = Array.isArray(value) ? sortUniqueStrings(value) : [];
      setAlbumFields((f) => ({
        ...f,
        [field]: nextValues,
      }));
      return;
    }
    setAlbumFields((f) => ({
      ...f,
      [field]: value,
    }));
  };
  const handleTrackChange = (id, fields) => {
    setTrackEdits((e) => ({
      ...e,
      [id]: fields,
    }));
  };
  const handleCreateTag = (tag) => {
    setTagOptions((opts) => {
      if (opts.includes(tag)) return opts;
      return [...opts, tag].sort((a, b) => a.localeCompare(b));
    });
  };
  const handleCreateGenre = async () => {
    const name = await dialogs.prompt("Enter new genre name:");
    if (!name) return;
    const genre = name.trim();
    if (!genre) return;
    setGenreOptions((opts) => sortUniqueStrings([...opts, genre]));
    setAlbumFields((fields) => {
      if (fields.genres.includes(genre)) return fields;
      return {
        ...fields,
        genres: sortUniqueStrings([...fields.genres, genre]),
      };
    });
  };
  const handleCreateSubGenre = async () => {
    const name = await dialogs.prompt("Enter new sub-genre name:");
    if (!name) return;
    const subGenre = name.trim();
    if (!subGenre) return;
    setSubGenreOptions((opts) => sortUniqueStrings([...opts, subGenre]));
    setAlbumFields((fields) => {
      if (fields.sub_genres.includes(subGenre)) return fields;
      return {
        ...fields,
        sub_genres: sortUniqueStrings([...fields.sub_genres, subGenre]),
      };
    });
  };
  const startFieldEdit = (field) => {
    setAlbumFields({
      title: album.title || "",
      genres: normalizeArrayField(album.genres),
      sub_genres: normalizeArrayField(album.sub_genres),
    });
    setEditingField(field);
    setEditMode("album");
  };
  const prepareTrackEdit = (fields) => {
    const allowed = { ...fields };
    if (!canEditDuration) delete allowed.duration;
    return trackUpdatePayload(allowed);
  };
  const saveTrackChanges = () => {
    if (savePending.current) return;
    const cleanedEdits = {};
    let tracks;
    try {
      tracks = Object.entries(trackEdits).map(([id, data]) => {
        const cleaned = prepareTrackEdit(data);
        cleanedEdits[id] = cleaned;
        return { ...cleaned, id: requestId(id) };
      });
    } catch (error) {
      dialogs.alert("Error updating tracks: " + error.message);
      return;
    }
    if (tracks.length === 0) {
      setEditMode("view");
      return;
    }
    savePending.current = true;
    setSaving(true);
    fetchJson(`${API_BASE}/tracks/bulk/`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        tracks,
      }),
      credentials: "include",
    })
      .then(() => {
        setAlbum((a) => ({
          ...a,
          tracks: a.tracks.map((t) =>
            cleanedEdits[t.id]
              ? {
                  ...t,
                  ...cleanedEdits[t.id],
                }
              : t,
          ),
        }));
        setTrackEdits({});
        setEditMode("view");
      })
      .catch((err) => dialogs.alert("Error updating tracks: " + err.message))
      .finally(() => {
        savePending.current = false;
        setSaving(false);
      });
  };
  const saveSingleTrack = async (id, fields) => {
    const cleaned = prepareTrackEdit(fields);
    return fetchJson(`${API_BASE}/tracks/bulk/`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        tracks: [
          {
            ...cleaned,
            id: requestId(id),
          },
        ],
      }),
      credentials: "include",
    })
      .then(() => {
        setAlbum((a) => ({
          ...a,
          tracks: a.tracks.map((t) =>
            t.id === id
              ? {
                  ...t,
                  ...cleaned,
                }
              : t,
          ),
        }));
      })
      .catch((err) => {
        dialogs.alert("Error updating track: " + err.message);
        throw err;
      });
  };
  const saveAlbumField = () => {
    if (!editingField || savePending.current) return;
    const payload = {};
    if (editingField === "title" && canEditTitle) {
      const val = titleRef.current.value.trim();
      if (!val) {
        dialogs.alert("Album title is required");
        return;
      }
      payload.title = val;
    }
    if (editingField === "genres" && canEditGenres) {
      payload.genres = sortUniqueStrings(albumFields.genres);
    }
    if (editingField === "sub_genres" && canEditSubGenres) {
      payload.sub_genres = sortUniqueStrings(albumFields.sub_genres);
    }
    if (Object.keys(payload).length === 0) {
      setEditMode("view");
      setEditingField(null);
      return;
    }
    savePending.current = true;
    setSaving(true);
    fetchJson(`${API_BASE}/albums/${albumId}/`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
      credentials: "include",
    })
      .then(() => {
        setAlbum((a) => ({
          ...a,
          ...(payload.title
            ? {
                title: payload.title,
              }
            : {}),
          ...(payload.genres
            ? {
                genres: payload.genres,
              }
            : {}),
          ...(payload.sub_genres
            ? {
                sub_genres: payload.sub_genres,
              }
            : {}),
        }));
        setEditMode("view");
        setEditingField(null);
      })
      .catch((err) => dialogs.alert("Error updating album: " + err.message))
      .finally(() => {
        savePending.current = false;
        setSaving(false);
      });
  };
  const toggleStyrene = () => {
    if (savePending.current) return;
    savePending.current = true;
    setSaving(true);
    const isStyrene = !album.is_styrene;
    fetchJson(`${API_BASE}/albums/${albumId}/`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        is_styrene: isStyrene,
      }),
      credentials: "include",
    })
      .then(() => {
        setAlbum((a) => ({
          ...a,
          is_styrene: isStyrene,
        }));
      })
      .catch((err) => dialogs.alert("Error updating album: " + err.message))
      .finally(() => {
        setAlbumMenuOpen(false);
        savePending.current = false;
        setSaving(false);
      });
  };
  if (error) {
    return (
      <Alert role="alert" severity="error">
        Error loading album: {error.message}
        <Button className="btn" onClick={() => setRetry((value) => value + 1)}>
          Retry
        </Button>
      </Alert>
    );
  }
  if (!album) {
    return <div role="status">Loading...</div>;
  }
  return (
    <>
      {canManageCollection && (
        <CollectionActions
          album={album}
          settings={settings}
          menuControlled={!archived}
          requestedMode={collectionMode}
          onDialogClose={(completed) => {
            setCollectionMode(null);
            if (!completed)
              setTimeout(() =>
                albumMenuRef.current?.querySelector(".menu-toggle")?.focus(),
              );
          }}
          disabled={saving || editMode !== "view"}
          onChanged={(result) => {
            focusAfterChange.current = true;
            setAlbum((current) => ({
              ...current,
              ...result,
            }));
            setAlbumMenuOpen(false);
            setEditMode("view");
          }}
        />
      )}
      {archived && (
        <p>
          Archived release · <Link to="/archived">View Archived Releases</Link>
        </p>
      )}
      <div className={archived ? "archived-data" : undefined}>
        {optionError && !archived && (
          <Alert role="alert" severity="error">
            Some editing options could not be loaded: {optionError.message}{" "}
            <Button
              className="btn"
              onClick={() => setOptionRevision((value) => value + 1)}
            >
              Retry options
            </Button>
          </Alert>
        )}
        <div className="album-details">
          <AlbumQuantity quantity={album.quantity} />
          <div className="cover">
            <img
              className="album-image"
              src={`${API_BASE}/albums/${albumId}/image/?v=${imgVersion}`}
              alt={`${album.title} cover`}
              loading="lazy"
              width={300}
              height={300}
              style={{
                display: "block",
                marginBottom: "8px",
                objectFit: "cover",
              }}
            />
            <input
              type="file"
              disabled={archived}
              accept="image/*"
              style={{
                display: "none",
              }}
              ref={fileInputRef}
              onChange={handleFileChange}
            />
          </div>
          <div className="info">
            {editMode === "album" && editingField === "title" ? (
              <TextInput
                ref={titleRef}
                id="album-title-input"
                aria-label="Album title"
                className="album-edit-input"
                defaultValue={album.title}
                onChange={(e) =>
                  handleAlbumFieldChange("title", e.target.value)
                }
                multiline
              />
            ) : (
              <h2 className="album-title" tabIndex={-1} ref={detailsHeading}>
                {album.title}
                <span className="album-title-ending">
                  {!!album.year && (
                    <span className="year"> ({album.year})</span>
                  )}
                  {!archived && (
                    <MuiLink
                      className="discogs-release-link"
                      href={`https://www.discogs.com/release/${albumId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="none"
                      aria-label="View release on Discogs (opens in a new tab)"
                      title="View release on Discogs"
                    >
                      <OpenInNew fontSize="inherit" />
                    </MuiLink>
                  )}
                </span>
              </h2>
            )}

            {album.artists && album.artists.length > 0 ? (
              <h3 className="album-artist">
                {album.artists.map((artist, index) => (
                  <React.Fragment
                    key={
                      (typeof artist === "object" && artist.id) ??
                      `${typeof artist === "object" ? artist.name : artist}-${index}`
                    }
                  >
                    {index > 0 && ", "}
                    {typeof artist === "object" && artist.id && !archived ? (
                      <Link className="artist-link" to={`/artist/${artist.id}`}>
                        {artist.name}
                      </Link>
                    ) : typeof artist === "object" ? (
                      artist.name
                    ) : (
                      artist
                    )}
                  </React.Fragment>
                ))}
              </h3>
            ) : (
              (album.artist || album.artist_name) && (
                <h3 className="album-artist">
                  {album.artist || album.artist_name}
                </h3>
              )
            )}

            {canEditGenres ||
            (Array.isArray(album.genres) && album.genres.length > 0) ? (
              editMode === "album" && editingField === "genres" ? (
                <MultiSelectDropdown
                  buttonId="album-genres-input"
                  options={genreOptions}
                  selected={albumFields.genres}
                  onChange={(value) => handleAlbumFieldChange("genres", value)}
                  label="Genres"
                  searchable
                  showLabel={false}
                  allLabel="Select Genres"
                  creatable
                  createLabel="+ Create Genre"
                  onCreate={handleCreateGenre}
                  showSelected
                  className="album-genre-dropdown"
                />
              ) : (
                <h3 className="genres">
                  {Array.isArray(album.genres)
                    ? album.genres
                        .slice()
                        .sort((a, b) => a.localeCompare(b))
                        .join(", ") || "No genres"
                    : album.genres || "No genres"}
                </h3>
              )
            ) : null}

            {canEditSubGenres ||
            (Array.isArray(album.sub_genres) && album.sub_genres.length > 0) ? (
              editMode === "album" && editingField === "sub_genres" ? (
                <MultiSelectDropdown
                  buttonId="album-sub-genres-input"
                  options={subGenreOptions}
                  selected={albumFields.sub_genres}
                  onChange={(value) =>
                    handleAlbumFieldChange("sub_genres", value)
                  }
                  label="Sub-Genres"
                  searchable
                  showLabel={false}
                  allLabel="Select Sub-Genres"
                  creatable
                  createLabel="+ Create Sub-Genre"
                  onCreate={handleCreateSubGenre}
                  showSelected
                  className="album-genre-dropdown"
                />
              ) : (
                <h3 className="sub-genres">
                  {Array.isArray(album.sub_genres)
                    ? album.sub_genres
                        .slice()
                        .sort((a, b) => a.localeCompare(b))
                        .join(", ") || "No sub-genres"
                    : album.sub_genres || "No sub-genres"}
                </h3>
              )
            ) : null}

            {album.labels && editMode !== "album" && (
              <h3 className="labels">
                {Array.isArray(album.labels)
                  ? album.labels.join(", ")
                  : album.labels}
              </h3>
            )}

            {album.is_styrene && editMode !== "album" && (
              <h4 className="styrene-subtitle">Styrene Pressing</h4>
            )}

            {editMode === "tracks" && (
              <div className="update-buttons">
                <Button
                  className="action-btn btn"
                  onClick={trackForm.handleSubmit(saveTrackChanges)}
                  disabled={saving || Object.keys(trackEdits).length === 0}
                >
                  Save Tracks
                </Button>
                <Button
                  className="action-btn btn"
                  disabled={saving}
                  onClick={() => {
                    setTrackEdits({});
                    setEditMode("view");
                  }}
                >
                  Cancel
                </Button>
              </div>
            )}
            {editMode === "album" && (
              <div className="update-buttons">
                <Button
                  className="action-btn btn"
                  onClick={albumForm.handleSubmit(saveAlbumField)}
                  disabled={saving}
                >
                  Save
                </Button>
                <Button
                  className="action-btn btn"
                  disabled={saving}
                  onClick={() => {
                    setEditMode("view");
                    setEditingField(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>
          {editMode === "view" && hasAlbumMenuOptions && (
            <AlbumOptionsMenu
              key={`${username}:${albumId}`}
              albumId={albumId}
              username={username}
              canPrintLabel={Boolean(
                username && canManageCollection && !archived,
              )}
              open={albumMenuOpen}
              onToggle={handleAlbumMenuToggle}
              onClose={() => setAlbumMenuOpen(false)}
              disabled={saving}
              menuRef={albumMenuRef}
              bulkEditAction={
                <MenuItem
                  className="desktop-only"
                  onClick={() => {
                    setTrackEdits({});
                    setEditMode("tracks");
                    setAlbumMenuOpen(false);
                  }}
                  component="button"
                >
                  Bulk Edit Tracks
                </MenuItem>
              }
            >
              {canManageCollection && (
                <MenuItem
                  onClick={() => {
                    setAlbumMenuOpen(false);
                    setCollectionMode("quantity");
                  }}
                  component="button"
                >
                  Update Quantity
                </MenuItem>
              )}
              {canManageCollection && (
                <MenuItem
                  sx={{ color: "error.main" }}
                  onClick={() => {
                    setAlbumMenuOpen(false);
                    setCollectionMode("remove");
                  }}
                  component="button"
                >
                  Delete Release
                </MenuItem>
              )}
              <Divider />
              {canEditTitle && (
                <MenuItem
                  onClick={() => {
                    startFieldEdit("title");
                    setTimeout(() =>
                      document.getElementById("album-title-input")?.focus(),
                    );
                    setAlbumMenuOpen(false);
                  }}
                  component="button"
                >
                  Rename Album
                </MenuItem>
              )}
              {canEditGenres && (
                <MenuItem
                  onClick={() => {
                    startFieldEdit("genres");
                    setTimeout(() =>
                      document.getElementById("album-genres-input")?.focus(),
                    );
                    setAlbumMenuOpen(false);
                  }}
                  component="button"
                >
                  Update Genres
                </MenuItem>
              )}
              {canEditSubGenres && (
                <MenuItem
                  onClick={() => {
                    startFieldEdit("sub_genres");
                    setTimeout(() =>
                      document
                        .getElementById("album-sub-genres-input")
                        ?.focus(),
                    );
                    setAlbumMenuOpen(false);
                  }}
                  component="button"
                >
                  Update Sub-Genres
                </MenuItem>
              )}
              {canEditAlbum && (
                <MenuItem onClick={toggleStyrene} component="button">
                  Set Styrene {album.is_styrene ? "False" : "True"}
                </MenuItem>
              )}
              {canEditAlbum && (
                <MenuItem
                  onClick={() => {
                    setAlbumMenuOpen(false);
                    setTimeout(() => fileInputRef.current?.click());
                  }}
                  component="button"
                >
                  Replace Image
                </MenuItem>
              )}
            </AlbumOptionsMenu>
          )}
        </div>
        <h3>Tracks</h3>
        <div className="tracklist album-tracklist">
          <Table
            className={`track-table album-track-table${editMode !== "tracks" ? " has-track-actions" : ""}`}
          >
            <TableHead>
              <TableRow>
                <TableCell component="th">#</TableCell>
                <TableCell component="th">Title</TableCell>
                <TableCell component="th">Duration</TableCell>
                <TableCell component="th">BPM</TableCell>
                <TableCell component="th">Rating</TableCell>
                <TableCell component="th">Notes</TableCell>
                <TableCell component="th">Tags</TableCell>
                {editMode !== "tracks" && <TableCell component="th" />}
              </TableRow>
            </TableHead>
            <TableBody>
              {album.tracks.map((track) =>
                editMode === "tracks" ? (
                  <TrackRow
                    key={track.id}
                    disabled={saving}
                    track={track}
                    onChange={handleTrackChange}
                    canEditDuration={canEditDuration}
                    canEditTrackInfo={canEditTrackInfo}
                    tagOptions={tagOptions}
                    onCreateTag={handleCreateTag}
                    highlight={String(track.id) === highlightTrack}
                    breakOptions={breakOptions}
                  />
                ) : (
                  <TrackRowReadOnly
                    key={`${track.id}:${archived}`}
                    readOnly={archived}
                    track={track}
                    role={role}
                    onSave={saveSingleTrack}
                    canEditDuration={canEditDuration}
                    canEditTrackInfo={canEditTrackInfo}
                    tagOptions={tagOptions}
                    onCreateTag={handleCreateTag}
                    highlight={String(track.id) === highlightTrack}
                    breakOptions={breakOptions}
                  />
                ),
              )}
            </TableBody>
          </Table>
          <div className="track-cards">
            {album.tracks.map((track) => (
              <TrackCard
                key={`${track.id}:${archived}`}
                readOnly={archived}
                track={track}
                onSave={saveSingleTrack}
                canEditDuration={canEditDuration}
                canEditTrackInfo={canEditTrackInfo}
                role={role}
                tagOptions={tagOptions}
                onCreateTag={handleCreateTag}
                highlight={String(track.id) === highlightTrack}
                breakOptions={breakOptions}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
export default function AlbumDetails(props) {
  return (
    <AlbumDetailsContent
      key={`${props.albumId}:${props.role}:${props.username}`}
      {...props}
    />
  );
}
